import { LoggingService } from "../loggingSvc";
import { AuditService } from "../audit/audit-service";
import { PerformanceMeasureService } from "../performance-logger/performance-measure";
import { PerformanceEventRecorder } from "../performance-logger/performance-event-recorder";
import { SignInReauthorizationService } from "../signInReauthorizationService";
import { MsalAuthenticationServiceImplementation } from "../msal/msal-authentication-service";

const loginEventAuditName = "SessionStarted";
export const msalErrorForInteractiveLogin = "interaction_required";
export const msalErrorForLoginRequired = "login_required";
export const msalErrorLocalStorageKeyName = "msal.error";

export interface AuthenticationLoggerService {
  recordLogoutStarted(): void;
}

export class AuthenticationLoggerServiceImpl implements AuthenticationLoggerService {
  private loginEvent: PerformanceEventRecorder;
  private logoutEvent: PerformanceEventRecorder;
  private renewalEvent: PerformanceEventRecorder;
  // @ngInject
  constructor(
    private $rootScope: ng.IRootScopeService,
    private msalAuthenticationService: MsalAuthenticationServiceImplementation,
    private loggingService: LoggingService,
    private $window: ng.IWindowService,
    private auditService: AuditService,
    private performanceMeasure: PerformanceMeasureService,
    private signInReauthorizationService: SignInReauthorizationService
  ) {
    this.loginEvent = this.performanceMeasure.getOrCreateEventRecorder("Login", {
      isCrossWindowOrTab: true,
      recordOncePerAppLifetime: true,
      shouldIncludeBootstrapTimeOnStart: true,
      shouldReportNonWallClockTime: true,
      description: "The login via MSAL experience"
    });
    this.logoutEvent = this.performanceMeasure.getOrCreateEventRecorder("Logout", {
      isCrossWindowOrTab: true,
      recordOncePerAppLifetime: true,
      shouldReportNonWallClockTime: true,
      description: "The log out via MSAL experience"
    });
    this.renewalEvent = this.performanceMeasure.getOrCreateEventRecorder("Renewal", {
      description: "Msal token renewal"
    });

    this.$rootScope.$on("$stateChangeSuccess", this.onStateChangeSuccess.bind(this));
    this.$rootScope.$on("msal:loginRedirect", this.onMsalLoginRedirect.bind(this));
    this.$rootScope.$on("msal:acquireTokenStarted", this.onMsalTokenRenewalStarted.bind(this));
    this.$rootScope.$on("msal:acquireTokenSuccess", this.onMsalTokenRenewalSuccess.bind(this));
    this.$rootScope.$on("msal:acquireTokenFailure", this.onMsalTokenRenewalFailure.bind(this));
    this.$rootScope.$on("msal:tokenRenewalSuccess", this.onMsalTokenRenewalSuccess.bind(this));
    this.$rootScope.$on("msal:tokenRenewalFailure", this.onMsalTokenRenewalFailure.bind(this));
    this.$window.addEventListener("storage", (event: StorageEvent) => {
      if (localStorageKeysToMonitor.includes(event.key)) {
        this.loggingService.logInfo(
          `Saw value change for ${event.key}, at ${event.url} where '${event.oldValue}' became '${event.newValue}'`
        );
      }
    });
  }

  public onStateChangeSuccess(
    event: ng.IAngularEvent,
    toState: ng.ui.IState,
    toParam: any,
    fromState: ng.ui.IState,
    fromParam: any
  ) {
    if (this.isSuccessfulLoginComplete(fromState) && this.loginEvent.success()) {
      this.auditService.recordAction(loginEventAuditName);
    }

    if (this.isLogoutComplete(toState, fromState)) {
      this.logoutEvent.success();
    }
  }

  public onMsalTokenRenewalSuccess() {
    this.renewalEvent.success();
  }

  public onMsalTokenRenewalStarted() {
    this.renewalEvent.start();
  }

  public onMsalTokenRenewalFailure(event: ng.IAngularEvent, errorDescription: string, error: string) {
    this.renewalEvent.failure(error + " " + errorDescription);
    localStorage[msalErrorLocalStorageKeyName] = error;
    // in case of consent missing, we should not open the reauth window.
    if (
      error == msalErrorForLoginRequired ||
      (error == msalErrorForInteractiveLogin && !errorDescription.includes("AADSTS65001"))
    ) {
      this.signInReauthorizationService.openReauthorizationWindow(false, error);
    }
  }

  public onMsalLoginRedirect() {
    this.loginEvent.start();
  }

  public recordLogoutStarted() {
    this.logoutEvent.start();
  }

  private isFromExternalSite(state: ng.ui.IState) {
    return state && state.name === "" && state.url === "^";
  }

  private isSuccessfulLoginComplete(fromState: ng.ui.IState): boolean {
    return this.isFromExternalSite(fromState) && this.msalAuthenticationService.userInfo.isAuthenticated;
  }

  private isLogoutComplete(toState: ng.ui.IState, fromState: ng.ui.IState) {
    return (
      this.isFromExternalSite(fromState) &&
      toState.name === "logout" &&
      !this.msalAuthenticationService.userInfo.isAuthenticated
    );
  }

  public static attachMsalLoggerToWindow(
    $window: ng.IWindowService,
    loggingService: LoggingService,
    maxMessagesToLogFromMsal: number
  ) {
    // See https://github.com/AzureAD/azure-activedirectory-library-for-js/blob/dev/lib/adal.js#L176 for details
    let iframeAnnotation = $window != $window.top ? "-iframe" : "";
    (<any>$window).Logging = {
      level: 3,
      loggedMessageCount: 0,
      loggingService: loggingService,
      log: (message: string) => {
        if ((<any>$window).Logging.loggedMessageCount >= maxMessagesToLogFromMsal) {
          return;
        }

        (<any>$window).Logging.loggedMessageCount++;
        loggingService.logInfo(`MSAL${iframeAnnotation}: ${message}`);

        if ((<any>$window).Logging.loggedMessageCount == maxMessagesToLogFromMsal) {
          loggingService.logInfo(
            `Logged ${maxMessagesToLogFromMsal} messages, preventing any more messages from being logged from msal.`
          );
        }
      }
    };

    loggingService.logInfo(
      "Attached Service Desk LoggingService to Msal logger, is in iframe:" + ($window != $window.top)
    );
  }
}

const localStorageKeysToMonitor = ["msal.state.login", "msal.session.state"];
